import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Chip, CircularProgress, Fade, IconButton, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { colors, CustomSwitch } from '../../components';
import { getUnmaskedAccountNumber, getUnmaskedRoutingNumber } from '../../services/bank-account-services';

export interface BankAccountCardProps {
    id: string;
    accountType: string;
    nickName?: string;
    bankName: string;
    routingNumber: string;
    accountNumber: string;
    IsActive?: boolean;
    AspireIsActive?: boolean;
    AutoPay?: boolean;
    isImpersonating?: boolean;
    onToggleStatus?: (accountId: string, currentStatus?: boolean) => void;
    onAspireInactiveClick?: () => void;
    onUnmaskError?: () => void;
}

const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '—';
    if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
};

const monoValueSx = {
    color: colors.fullBlack,
    fontWeight: 600,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

export const BankAccountCard: React.FC<BankAccountCardProps> = ({
    id,
    accountType,
    nickName,
    bankName,
    routingNumber,
    accountNumber,
    IsActive = true,
    AspireIsActive = true,
    AutoPay = false,
    isImpersonating = false,
    onToggleStatus,
    onAspireInactiveClick,
    onUnmaskError,
}) => {
    // Full number is kept only in component state and cleared when hidden — never stored elsewhere.
    const [unmaskedNumber, setUnmaskedNumber] = useState<string | null>(null);
    const [isUnmasking, setIsUnmasking] = useState(false);
    const [unmaskedRouting, setUnmaskedRouting] = useState<string | null>(null);
    const [isUnmaskingRouting, setIsUnmaskingRouting] = useState(false);

    const handleToggleRoutingVisibility = async () => {
        if (unmaskedRouting !== null) {
            setUnmaskedRouting(null);
            return;
        }
        // Only one of routing/account number is revealed at a time.
        setUnmaskedNumber(null);
        setIsUnmaskingRouting(true);
        try {
            setUnmaskedRouting(await getUnmaskedRoutingNumber(id));
        } catch {
            onUnmaskError?.();
        } finally {
            setIsUnmaskingRouting(false);
        }
    };

    const handleToggleVisibility = async () => {
        if (unmaskedNumber !== null) {
            setUnmaskedNumber(null);
            return;
        }
        // Only one of routing/account number is revealed at a time.
        setUnmaskedRouting(null);
        setIsUnmasking(true);
        try {
            setUnmaskedNumber(await getUnmaskedAccountNumber(id));
        } catch {
            onUnmaskError?.();
        } finally {
            setIsUnmasking(false);
        }
    };

    const isAspireInactive = AspireIsActive === false;
    const isActive = IsActive === true;
    const isChecking = accountType === 'Checking';
    // Tile and chip share the account-type colours (Checking = blue, Saving = amber).
    const typeBg = isChecking ? colors.bluePreset[80] : colors.warningPreset[90];
    const typeFg = isChecking ? colors.bluePreset[30] : colors.warningPreset[70];

    const switchElement = isAspireInactive ? (
        <CustomSwitch
            checked={false}
            onChange={() => {}}
            inactiveColor={colors.errorPreset[10]}
            sx={{ opacity: 0.45, pointerEvents: 'none', cursor: 'not-allowed' }}
        />
    ) : (
        <CustomSwitch
            checked={isActive}
            onChange={() => onToggleStatus?.(id, isActive)}
            inactiveColor={colors.errorPreset[10]}
            sx={{
                pointerEvents: isImpersonating ? 'none' : 'auto',
                cursor: isImpersonating ? 'not-allowed' : 'pointer',
            }}
        />
    );

    // Status badges sit in the header row next to the type chip.
    const inactiveChip = !isActive ? (
        <Chip
            label="Inactive"
            size="small"
            sx={{
                height: 22,
                backgroundColor: colors.greyPreset[10],
                color: colors.greyPreset[60],
                fontWeight: 500,
            }}
        />
    ) : null;

    const autoPayChip = AutoPay ? (
        <Chip
            label="Auto-Pay Enabled"
            size="small"
            sx={{
                height: 22,
                backgroundColor: '#ECFDF5',
                color: '#047857',
                border: '1px solid #A7F3D0',
                fontWeight: 500,
            }}
        />
    ) : null;

    return (
        <Box
            onClick={isAspireInactive ? onAspireInactiveClick : undefined}
            sx={{
                borderRadius: 4,
                border: `1px solid ${colors.greyPreset[20]}`,
                backgroundColor: colors.fullWhite,
                boxShadow: '0 4px 14px rgba(16, 24, 40, 0.12), 0 2px 4px rgba(16, 24, 40, 0.06)',
                padding: 2.5,
                cursor: isAspireInactive ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: '0 10px 28px rgba(16, 24, 40, 0.18), 0 4px 8px rgba(16, 24, 40, 0.08)' },
                opacity: isActive ? 1 : 0.6,
            }}
        >
            {/* Header: initials tile, bank name + type chip, toggle */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} gap={2}>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            flexShrink: 0,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: typeBg,
                            color: typeFg,
                            fontWeight: 700,
                            fontSize: '14px',
                        }}
                    >
                        {getInitials(bankName)}
                    </Box>
                    <Stack sx={{ minWidth: 0 }} gap={0.5} alignItems="flex-start">
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 700,
                                color: colors.fullBlack,
                                lineHeight: 1.3,
                                wordBreak: 'break-word',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {bankName}
                        </Typography>
                        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                            <Chip
                                label={isChecking ? 'CHECKING' : 'SAVING'}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    backgroundColor: typeBg,
                                    color: typeFg,
                                }}
                            />
                            {inactiveChip}
                            {autoPayChip}
                        </Stack>
                    </Stack>
                </Stack>
                <Box sx={{ flexShrink: 0 }}>{switchElement}</Box>
            </Stack>

            {/* Nickname */}
            <Typography variant="body2" sx={{ color: colors.greyPreset[50], mb: 2 }}>
                Nickname:{' '}
                <Box component="span" sx={{ color: colors.fullBlack, fontWeight: 500 }}>
                    {nickName || '—'}
                </Box>
            </Typography>

            {/* Details panel */}
            <Box
                sx={{
                    borderRadius: 3,
                    backgroundColor: colors.greyPreset[2.5],
                    border: `1px solid ${colors.greyPreset[20]}`,
                    px: 2,
                    py: 0.5,
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.75}>
                    <Typography variant="body2" sx={{ color: colors.greyPreset[50] }}>
                        Routing Number
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.25}>
                        <Fade key={unmaskedRouting === null ? 'masked' : 'unmasked'} in appear timeout={350}>
                            <Typography
                                variant="body2"
                                sx={{
                                    ...monoValueSx,
                                    opacity: isUnmaskingRouting ? 0.5 : 1,
                                    transition: 'opacity 0.2s ease',
                                }}
                            >
                                {unmaskedRouting ?? routingNumber}
                            </Typography>
                        </Fade>
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                handleToggleRoutingVisibility();
                            }}
                            disabled={isUnmaskingRouting}
                            aria-label={unmaskedRouting === null ? 'Show routing number' : 'Hide routing number'}
                        >
                            {isUnmaskingRouting ? (
                                <CircularProgress size={16} />
                            ) : unmaskedRouting === null ? (
                                <VisibilityIcon fontSize="medium" />
                            ) : (
                                <VisibilityOffIcon fontSize="medium" />
                            )}
                        </IconButton>
                    </Stack>
                </Stack>
                <Box sx={{ borderTop: `1px solid ${colors.greyPreset[20]}` }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.75}>
                    <Typography variant="body2" sx={{ color: colors.greyPreset[50] }}>
                        Account Number
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.25}>
                        {/* key remounts on mask/unmask so the number fades in instead of snapping */}
                        <Fade key={unmaskedNumber === null ? 'masked' : 'unmasked'} in appear timeout={350}>
                            <Typography
                                variant="body2"
                                sx={{
                                    ...monoValueSx,
                                    opacity: isUnmasking ? 0.5 : 1,
                                    transition: 'opacity 0.2s ease',
                                }}
                            >
                                {unmaskedNumber ?? accountNumber}
                            </Typography>
                        </Fade>
                        <IconButton
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                handleToggleVisibility();
                            }}
                            disabled={isUnmasking}
                            aria-label={unmaskedNumber === null ? 'Show account number' : 'Hide account number'}
                        >
                            {isUnmasking ? (
                                <CircularProgress size={16} />
                            ) : unmaskedNumber === null ? (
                                <VisibilityIcon fontSize="medium" />
                            ) : (
                                <VisibilityOffIcon fontSize="medium" />
                            )}
                        </IconButton>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};
